import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Avatar,
  Divider,
  Timeline,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Spin,
  Progress,
  Descriptions,
  App
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  FileTextOutlined,
  ContactsOutlined,
  DollarOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import useTaskStore from '../store/taskStore';
import useAuthStore from '../store/authStore';
import apiService from '../services/apiService';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TaskDetailView = () => {
  const { message: messageApi } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { updateTaskStatus, updateTaskPriority } = useTaskStore();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [priorityModalVisible, setPriorityModalVisible] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  
  const [editForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [priorityForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      fetchTaskDetails();
      fetchTaskTimeline();
      loadTeamMembers();
    }
  }, [id]);

  const fetchTaskDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/tasks/${id}`);
      if (response.data.success) {
        setTask(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        messageApi.error('Authentication failed. Please log in again.');
        navigate('/auth/login');
        return;
      }
      messageApi.error('Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskTimeline = async () => {
    try {
      const response = await apiService.get(`/tasks/${id}/timeline`);
      if (response.data.success) {
        setTimeline(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch task timeline:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        messageApi.error('Authentication failed. Please log in again.');
        navigate('/auth/login');
        return;
      }
      // Create a basic timeline from task data if API doesn't exist
      if (task) {
        const basicTimeline = [
          {
            id: 1,
            action: 'created',
            description: 'Task created',
            user: task.createdBy,
            timestamp: task.createdAt,
            type: 'create'
          }
        ];
        setTimeline(basicTimeline);
      }
    }
  };

  const loadTeamMembers = async () => {
    setLoadingTeamMembers(true);
    try {
      const response = await apiService.get('/users/team-members');
      if (response.data.success) {
        setTeamMembers(response.data.data.teamMembers);
      }
    } catch (error) {
      console.error('Failed to load team members:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        messageApi.error('Authentication failed. Please log in again.');
        navigate('/auth/login');
        return;
      }
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const handleEditTask = async (values) => {
    try {
      const response = await apiService.put(`/tasks/${id}`, {
        title: values.title,
        description: values.description,
        priority: values.priority,
        status: values.status,
        dueDate: values.dueDate ? values.dueDate.toISOString() : null,
        assignedTo: values.assignedTo || null,
        notes: values.notes || ''
      });

      if (response.data.success) {
        setTask(response.data.data);
        messageApi.success('Task updated successfully');
        setEditModalVisible(false);
        editForm.resetFields();
        fetchTaskTimeline(); // Refresh timeline
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        messageApi.error('Authentication failed. Please log in again.');
        navigate('/auth/login');
        return;
      }
      messageApi.error('Failed to update task');
    }
  };

  const handleStatusUpdate = async (values) => {
    try {
      await updateTaskStatus(id, values.status, values.notes);
      messageApi.success('Task status updated successfully');
      setStatusModalVisible(false);
      statusForm.resetFields();
      fetchTaskDetails();
      fetchTaskTimeline();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        messageApi.error('Authentication failed. Please log in again.');
        navigate('/auth/login');
        return;
      }
      messageApi.error('Failed to update task status');
    }
  };

  const handlePriorityUpdate = async (values) => {
    try {
      await updateTaskPriority(id, values.priority);
      messageApi.success('Task priority updated successfully');
      setPriorityModalVisible(false);
      priorityForm.resetFields();
      fetchTaskDetails();
      fetchTaskTimeline();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        messageApi.error('Authentication failed. Please log in again.');
        navigate('/auth/login');
        return;
      }
      messageApi.error('Failed to update task priority');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'in_progress': return 'blue';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <FireOutlined />;
      case 'high': return <ExclamationCircleOutlined />;
      case 'medium': return <ClockCircleOutlined />;
      case 'low': return <CheckCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getSourceIcon = (sourceType) => {
    return sourceType === 'lead' ? <ContactsOutlined /> : <DollarOutlined />;
  };

  const isOverdue = (dueDate, status) => {
    return moment(dueDate).isBefore(moment()) && status !== 'completed';
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'create': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'status_change': return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      case 'priority_change': return <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />;
      case 'assignment': return <UserOutlined style={{ color: '#722ed1' }} />;
      case 'update': return <EditOutlined style={{ color: '#13c2c2' }} />;
      default: return <HistoryOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-8">
        <Text>Task not found</Text>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/tasks')}
          >
            Back to Tasks
          </Button>
          <div>
            <Title level={2} className="mb-2">{task.title}</Title>
            <div className="flex items-center space-x-4">
              <Tag color={getStatusColor(task.status)}>
                {task.status?.replace('_', ' ').toUpperCase()}
              </Tag>
              <Tag color={getPriorityColor(task.priority)} icon={getPriorityIcon(task.priority)}>
                {task.priority?.toUpperCase()}
              </Tag>
              {isOverdue(task.dueDate, task.status) && (
                <Tag color="red">OVERDUE</Tag>
              )}
            </div>
          </div>
        </div>
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              editForm.setFieldsValue({
                title: task.title,
                description: task.description,
                priority: task.priority,
                status: task.status,
                dueDate: task.dueDate ? moment(task.dueDate) : null,
                assignedTo: task.assignedTo?._id,
                notes: task.notes
              });
              setEditModalVisible(true);
            }}
          >
            Edit Task
          </Button>
        </Space>
      </div>

      {/* Main Content - 70:30 Layout */}
      <Row gutter={24}>
        {/* Task Details - 70% */}
        <Col span={17}>
          <Card className="shadow-soft">
            <div className="space-y-6">
              {/* Task Info */}
              <div>
                <Title level={4}>Task Information</Title>
                <Descriptions column={2} bordered>
                  <Descriptions.Item label="Source" span={2}>
                    <div className="flex items-center space-x-2">
                      {getSourceIcon(task.sourceType)}
                      <span>{task.sourceName}</span>
                      {task.sourceCompany && <span>• {task.sourceCompany}</span>}
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created By">
                    <div className="flex items-center space-x-2">
                      <Avatar size="small" icon={<UserOutlined />} />
                      <span>{task.createdBy?.firstName} {task.createdBy?.lastName}</span>
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Assigned To">
                    {task.assignedTo ? (
                      <div className="flex items-center space-x-2">
                        <Avatar size="small" icon={<UserOutlined />} />
                        <span>{task.assignedTo.firstName} {task.assignedTo.lastName}</span>
                      </div>
                    ) : (
                      <Text type="secondary">Unassigned</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {moment(task.createdAt).format('MMM DD, YYYY hh:mm A')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Due Date">
                    <div className={isOverdue(task.dueDate, task.status) ? 'text-red-500 font-medium' : ''}>
                      {moment(task.dueDate).format('MMM DD, YYYY hh:mm A')}
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Progress" span={2}>
                    <Progress 
                      percent={
                        task.status === 'completed' ? 100 : 
                        task.status === 'in_progress' ? 50 : 
                        task.status === 'cancelled' ? 0 : 10
                      }
                      status={task.status === 'cancelled' ? 'exception' : 'normal'}
                      strokeColor={task.status === 'completed' ? '#52c41a' : '#1890ff'}
                    />
                  </Descriptions.Item>
                </Descriptions>
              </div>

              {/* Description */}
              {task.description && (
                <div>
                  <Title level={4}>Description</Title>
                  <Card>
                    <Paragraph>{task.description}</Paragraph>
                  </Card>
                </div>
              )}

              {/* Notes */}
              {task.notes && (
                <div>
                  <Title level={4}>Notes</Title>
                  <Card>
                    <Paragraph>{task.notes}</Paragraph>
                  </Card>
                </div>
              )}

              {/* Quick Actions */}
              <div>
                <Title level={4}>Quick Actions</Title>
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => {
                      statusForm.setFieldsValue({
                        status: task.status,
                        notes: task.notes || ''
                      });
                      setStatusModalVisible(true);
                    }}
                  >
                    Update Status
                  </Button>
                  <Button
                    icon={<ExclamationCircleOutlined />}
                    onClick={() => {
                      priorityForm.setFieldsValue({
                        priority: task.priority
                      });
                      setPriorityModalVisible(true);
                    }}
                  >
                    Change Priority
                  </Button>
                </Space>
              </div>
            </div>
          </Card>
        </Col>

        {/* Timeline - 30% */}
        <Col span={7}>
          <Card className="shadow-soft" title={
            <div className="flex items-center space-x-2">
              <HistoryOutlined />
              <span>Task Timeline</span>
            </div>
          }>
            <Timeline
              items={timeline.map(item => ({
                dot: getTimelineIcon(item.type),
                children: (
                  <div>
                    <div className="font-medium">{item.description}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {item.user && (
                        <span>{item.user.firstName} {item.user.lastName} • </span>
                      )}
                      {moment(item.timestamp).format('MMM DD, YYYY hh:mm A')}
                    </div>
                    {item.details && (
                      <div className="text-sm text-gray-600 mt-1">
                        {item.details}
                      </div>
                    )}
                  </div>
                )
              }))}
            />
            {timeline.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                <HistoryOutlined className="text-2xl mb-2" />
                <div>No timeline data available</div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Edit Task Modal */}
      <Modal
        title="Edit Task"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditTask}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter task title' }]}
          >
            <Input placeholder="Enter task title" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter task description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
              >
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
              >
                <Select>
                  <Option value="pending">Pending</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="dueDate"
            label="Due Date"
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="assignedTo" label="Assign To Team Member">
            <Select
              placeholder="Select team member"
              allowClear
              loading={loadingTeamMembers}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {teamMembers.map(member => (
                <Select.Option key={member.id} value={member.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar size="small" src={member.avatar}>
                      {member.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <span>{member.name}</span>
                    <span style={{ color: '#999', fontSize: '12px' }}>({member.role})</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea rows={2} placeholder="Additional notes" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Task
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Status Update Modal */}
      <Modal
        title="Update Task Status"
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          statusForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusUpdate}
        >
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select>
              <Option value="pending">Pending</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea
              rows={4}
              placeholder="Add any notes about this status change..."
            />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setStatusModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Status
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Priority Update Modal */}
      <Modal
        title="Update Task Priority"
        open={priorityModalVisible}
        onCancel={() => {
          setPriorityModalVisible(false);
          priorityForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={priorityForm}
          layout="vertical"
          onFinish={handlePriorityUpdate}
        >
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Please select a priority' }]}
          >
            <Select>
              <Option value="urgent">
                <span className="flex items-center space-x-2">
                  <FireOutlined style={{ color: '#ff4d4f' }} />
                  <span>Urgent</span>
                </span>
              </Option>
              <Option value="high">
                <span className="flex items-center space-x-2">
                  <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                  <span>High</span>
                </span>
              </Option>
              <Option value="medium">
                <span className="flex items-center space-x-2">
                  <ClockCircleOutlined style={{ color: '#1890ff' }} />
                  <span>Medium</span>
                </span>
              </Option>
              <Option value="low">
                <span className="flex items-center space-x-2">
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>Low</span>
                </span>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setPriorityModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Priority
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TaskDetailView;
